import { Container, Text, TextStyle, Graphics } from "pixi.js";

export class FPSMeter extends Container {
  private fpsText: Text;
  private frameTimeText: Text;
  private memoryText: Text;
  private framesElapsedText: Text;
  private background: Graphics;
  
  private frameCount: number = 0;
  private lastUpdateTime: number = 0;
  private lastFrameTime: number = 0;
  private frameTimeSum: number = 0;
  private frameTimeSamples: number = 0;
  private framesElapsed: number = 0;
  private fpsFrameCount: number = 0; // Frame count for FPS calculation
  
  private readonly updateInterval: number = 1000; // Update FPS and frame time every 1 second
  private readonly padding: number = 8; // Padding around text
  private readonly lineHeight: number = 20; // Height between lines

  constructor() {
    super();
    this.name = "FPSMeter";
    
    // Create background
    this.background = new Graphics();
    this.background.alpha = 0.8;
    this.addChild(this.background);
    
    // Create text style
    const style = new TextStyle({
      fontFamily: "monospace",
      fontSize: 14,
      fill: 0x00ff00,
      fontWeight: "bold",
      stroke: 0x000000,
      strokeThickness: 2,
    });

    // Create text elements
    this.fpsText = new Text({ text: "FPS: --", style });
    this.frameTimeText = new Text({ text: "Frame Time: -- ms", style });
    this.memoryText = new Text({ text: "Memory: -- MB", style });
    this.framesElapsedText = new Text({ text: "Frames: 0", style });

    // Position texts vertically
    this.fpsText.x = this.padding;
    this.fpsText.y = this.padding;
    
    this.frameTimeText.x = this.padding;
    this.frameTimeText.y = this.padding + this.lineHeight;
    
    this.memoryText.x = this.padding;
    this.memoryText.y = this.padding + this.lineHeight * 2;
    
    this.framesElapsedText.x = this.padding;
    this.framesElapsedText.y = this.padding + this.lineHeight * 3;

    // Add all texts to container
    this.addChild(this.fpsText);
    this.addChild(this.frameTimeText);
    this.addChild(this.memoryText);
    this.addChild(this.framesElapsedText);

    // Draw background
    this.updateBackground();

    // Initialize timing
    this.lastUpdateTime = performance.now();
    this.lastFrameTime = 0;
  }

  private updateBackground(): void {
    // Calculate background size based on text bounds
    const maxWidth = Math.max(
      this.fpsText.width,
      this.frameTimeText.width,
      this.memoryText.width,
      this.framesElapsedText.width
    );
    const totalHeight = this.padding * 2 + this.lineHeight * 4;
    const totalWidth = maxWidth + this.padding * 2;

    // Draw dark background rectangle
    this.background.clear();
    this.background.rect(0, 0, totalWidth, totalHeight);
    this.background.fill(0x000000);
  }

  /**
   * Update FPS meter - should be called every frame
   */
  update(deltaTime: number): void {
    const currentTime = performance.now();
    
    // Calculate actual frame time from performance.now() for accuracy
    let frameTime: number;
    if (this.lastFrameTime === 0) {
      frameTime = deltaTime * 16.67; // First frame, use delta estimate
    } else {
      frameTime = currentTime - this.lastFrameTime;
    }
    this.lastFrameTime = currentTime;
    
    this.frameCount++;
    this.framesElapsed++;
    this.fpsFrameCount++;
    this.frameTimeSum += frameTime;
    this.frameTimeSamples++;

    // Update memory usage
    this.updateMemoryDisplay();

    // Update frames elapsed
    this.framesElapsedText.text = `Frames: ${this.framesElapsed}`;

    // Update FPS and frame time every 1 second
    const timeSinceLastUpdate = currentTime - this.lastUpdateTime;
    if (timeSinceLastUpdate >= this.updateInterval) {
      // Calculate FPS as frames per second
      const fps = Math.round((this.fpsFrameCount / timeSinceLastUpdate) * 1000);
      this.fpsText.text = `FPS: ${fps}`;
      
      // Calculate average frame time
      const avgFrameTime = this.frameTimeSum / this.frameTimeSamples;
      this.frameTimeText.text = `Frame Time: ${avgFrameTime.toFixed(2)} ms`;
      
      // Update background size when text changes
      this.updateBackground();
      
      // Reset counters
      this.frameTimeSum = 0;
      this.frameTimeSamples = 0;
      this.fpsFrameCount = 0;
      this.lastUpdateTime = currentTime;
    }
  }

  private updateMemoryDisplay(): void {
    // Check if performance.memory is available (Chrome/Edge)
    const oldText = this.memoryText.text;
    if ((performance as any).memory) {
      const memory = (performance as any).memory;
      const usedMB = (memory.usedJSHeapSize / 1048576).toFixed(2);
      const totalMB = (memory.totalJSHeapSize / 1048576).toFixed(2);
      this.memoryText.text = `Memory: ${usedMB} / ${totalMB} MB`;
    } else {
      // Fallback for browsers without performance.memory
      this.memoryText.text = `Memory: N/A`;
    }
    
    // Update background if text changed (memory values can change width)
    if (oldText !== this.memoryText.text) {
      this.updateBackground();
    }
  }

  /**
   * Reset all counters
   */
  reset(): void {
    this.frameCount = 0;
    this.framesElapsed = 0;
    this.frameTimeSum = 0;
    this.frameTimeSamples = 0;
    this.fpsFrameCount = 0;
    this.lastUpdateTime = performance.now();
    this.lastFrameTime = 0;
  }
}

